import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SectionLabel from "@/components/SectionLabel";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { SITE, PRIMARY_PHONE } from "@/lib/site";
import { SERVICES } from "@/lib/services";

const FAQS = [
  { q: "How quickly can you arrive?", a: "In metro Victoria most jobs are reached within 30 minutes. Regional jobs depend on distance — we'll always give you an honest ETA when you call." },
  { q: "What's included in the $70 starting price?", a: "Our $70 starting price covers short, local tows for standard passenger vehicles. Longer distances and heavier vehicles are quoted upfront — no surprises." },
  { q: "Do you operate 24/7?", a: "Yes. We are open all day, every day, including weekends and public holidays." },
  { q: "Which areas do you cover?", a: "We service all of Victoria from our bases in Sale and Albion — including Melbourne, Geelong, Ballarat, Bendigo, Gippsland and beyond." },
  { q: "What kinds of vehicles can you tow?", a: "Cars, utes, vans, caravans, RVs, boats, motorcycles, light & heavy trucks, box trucks and equipment like forklifts." },
  { q: "Do you handle accident recovery?", a: "Yes. We offer flatbed accident recovery, scene management, and can liaise with police, panel shops and insurers." },
];

const Contact = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const payload = new URLSearchParams({
        "form-name": "quote-request",
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        service: form.service || "",
        message: form.message.trim(),
      });

      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      if (!response.ok) {
        throw new Error(`Form submission failed: ${response.status}`);
      }

      toast.success("Quote request sent! We'll be in touch shortly.");
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-10">
          <SectionLabel>Contact</SectionLabel>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[1.05] max-w-3xl">
            Need help right now? Pick up the phone — we'll be there.
          </h1>
          <p className="mt-5 max-w-2xl text-slate-600 leading-relaxed">
            We answer 24/7. For non-urgent enquiries, send the form below and we'll reply with a free quote shortly.
          </p>
        </div>
      </section>

      <section className="pb-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-12">
          {/* Contact details column */}
          <aside className="lg:col-span-5 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-7">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
                Phone — Click to call
              </p>
              <div className="mt-3 space-y-3">
                {SITE.phones.map((p) => (
                  <a
                    key={p.tel}
                    href={`tel:${p.tel}`}
                    data-testid={`contact-tel-${p.tel}`}
                    className="flex items-center gap-3 rounded-xl bg-white p-4 border border-slate-200 hover:-translate-y-0.5 hover:shadow-md transition-all"
                  >
                    <div className="grid h-11 w-11 place-items-center rounded-lg bg-[var(--color-secondary)] text-white">
                      <Phone className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-display font-extrabold text-xl text-slate-900 font-num">
                        {p.number}
                      </p>
                      <p className="text-xs text-slate-500">{p.label} line · 24/7</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-7 bg-white">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-[var(--color-primary)] mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Email</p>
                    <a
                      href={`mailto:${SITE.email}`}
                      data-testid="contact-email-link"
                      className="font-bold text-slate-900 hover:text-[var(--color-primary)] break-all"
                    >
                      {SITE.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-[var(--color-primary)] mt-0.5" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Hours</p>
                    <p className="font-bold text-slate-900">{SITE.availability}</p>
                  </div>
                </li>
                {SITE.locations.map((loc) => (
                  <li key={loc.address} className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[var(--color-primary)] mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{loc.city}</p>
                      <p className="font-bold text-slate-900">{loc.address}</p>
                      <p className="text-sm text-slate-500">{loc.region}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <iframe
                title="Vic Wide Towing — Sale, VIC location"
                data-testid="contact-map-sale"
                src={SITE.mapEmbeds.sale}
                width="100%"
                height="240"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <iframe
                title="Vic Wide Towing — Albion location"
                data-testid="contact-map-albion"
                src={SITE.mapEmbeds.albion}
                width="100%"
                height="240"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </aside>

          {/* Form column */}
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-10">
              {submitted ? (
                <div data-testid="contact-success-card" className="text-center py-10">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
                  <h3 className="mt-5 font-display text-3xl font-black tracking-tighter text-slate-900">
                    We've got your request.
                  </h3>
                  <p className="mt-3 text-slate-600 max-w-md mx-auto">
                    Our team will be in touch shortly. For urgent towing,
                    please call us right now.
                  </p>
                  <a
                    href={`tel:${PRIMARY_PHONE.tel}`}
                    data-testid="contact-success-call"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--color-secondary)] px-6 py-3 font-display font-extrabold text-white"
                  >
                    <Phone className="h-4 w-4" /> {PRIMARY_PHONE.number}
                  </a>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-success-new"
                    className="mt-3 block mx-auto text-sm text-slate-500 underline hover:text-slate-800"
                  >
                    Send another request
                  </button>
                </div>
              ) : (
                <form
                  name="quote-request"
                  method="POST"
                  data-netlify="true"
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  noValidate
                  className="space-y-5"
                >
                  <input type="hidden" name="form-name" value="quote-request" />
                  <div>
                    <h2 className="font-display text-2xl font-extrabold text-slate-900">
                      Request a free quote
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                      Tell us a little about the job and we'll get back to you fast.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact-name">Your name</Label>
                      <Input
                        id="contact-name"
                        name="name"
                        data-testid="contact-input-name"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Jane Smith"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-phone">Phone (optional)</Label>
                      <Input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="04xx xxx xxx"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="contact-email">Email</Label>
                    <Input
                      id="contact-email"
                      name="email"
                      data-testid="contact-input-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@email.com"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact-service">Service needed</Label>
                    <input type="hidden" name="service" value={form.service} />
                    <Select value={form.service} onValueChange={update("service")}>
                      <SelectTrigger
                        id="contact-service"
                        data-testid="contact-select-service"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICES.map((s) => (
                          <SelectItem key={s.slug} value={s.title}>
                            {s.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="contact-message">Tell us what's happening</Label>
                    <Textarea
                      id="contact-message"
                      name="message"
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="e.g. Sedan broken down on M1 near Pakenham, need a tow to my mechanic in Cranbourne."
                      rows={5}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={loading}
                    className="w-full bg-[var(--color-primary)] hover:bg-[var(--color-primary-soft)] text-white font-display font-extrabold py-6 text-base"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Sending…
                      </>
                    ) : (
                      "Send my free quote request"
                    )}
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    Or call us right now on{" "}
                    <a
                      href={`tel:${PRIMARY_PHONE.tel}`}
                      className="font-bold text-[var(--color-primary)] hover:underline"
                    >
                      {PRIMARY_PHONE.number}
                    </a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <SectionLabel>FAQ</SectionLabel>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-slate-900">
            Common questions
          </h2>
          <Accordion
            type="single"
            collapsible
            data-testid="contact-faq"
            className="mt-8 rounded-2xl border border-slate-200 bg-white px-2"
          >
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-slate-200">
                <AccordionTrigger
                  data-testid={`faq-trigger-${i}`}
                  className="text-left font-display font-bold text-slate-900 hover:no-underline px-4"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-content-${i}`}
                  className="text-slate-600 leading-relaxed px-4"
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Contact;
